import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Location } from 'history'
import queryString, { ParsedQuery } from 'query-string'
import Dropdown, { DropdownProps } from 'react-bootstrap/Dropdown'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import isFunction from 'lodash/isFunction'
import {
  FilterCapabilitiesContext,
  useFilterCapabilities,
} from '../FilterPermissions.context'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import { CheckBoxInput } from '../../../Inputs/CheckBox'
import { FilterName, FilterParam } from '../FilterBar.types'
import {
  FilterCountChip,
  FilterDropdownItem,
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from '../FilterBar.styles'
import { useFilterLabels } from '../FilterLabelsContext'
import { DropdownItemType, NestedFilterType } from './FilterDropdown.constants'
import {
  DropDownMenuWithNesting,
  FilterGroupHeading,
  NoFilterMatches,
  FilterOptionContainer,
  FilterDropdownIcon,
  FilterItemsContainer,
} from './FilterDropdown.styles'
import { NestedFilter } from './FilterDropdown.components'
import {
  FILTER_MENU_SEARCH_THRESHOLD,
  FilterAccordionBody,
  FilterAccordionChevron,
  FilterOptionLabel,
  countFilterOptions,
  filterOptionsBySearch,
  useFilterMenuSearch,
} from './FilterAccordion'
import { EnsureParent } from './FilterDropdown.types'

type ToggleMetadata = Parameters<NonNullable<DropdownProps['onToggle']>>[1]

type FilterDropdownBaseProps = {
  requiredCapabilities?: CAPABILITY[]
  filterParam: FilterParam
  filterName: FilterName
  filterValues: DropdownItemType[]
  isLoading?: boolean
  // What counts as selected when the URL has no value for this filter. The page
  // applying the filter must use the same default.
  defaultSelection?: string[]
  includeParentFilter?: boolean
  isNested?: boolean
  isSingleSelect?: boolean
  icon?: React.ReactNode
  /**
   * When set, checking a parent option writes ONLY the parent value
   * instead of enumerating every nested child, in order to keep the URL compact.
   */
  parentMeansAll?: boolean
}

const asArray = (value: string | string[] | null | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : []

type FilterDropdownConditionalProps =
  | {
      show: boolean
      handleToggle: (nextShow: boolean) => void
    }
  | {
      show?: undefined
      handleToggle?: undefined
    }

type FilterDropdownProps = FilterDropdownBaseProps &
  FilterDropdownConditionalProps

const normalizeBracketedKey = (key: string): string =>
  key.replace(/(\[\])+$/g, '')

const normalizeParsedQuery = (
  query: ParsedQuery<string>,
): ParsedQuery<string> => {
  const normalized: ParsedQuery<string> = {}
  const toArray = (value: string | string[] | null | undefined): string[] => {
    if (value === undefined || value === null) return []
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string')
    }
    return typeof value === 'string' ? [value] : []
  }

  Object.entries(query).forEach(([rawKey, rawValue]) => {
    const key = normalizeBracketedKey(rawKey)
    const mergedValues = [...toArray(normalized[key]), ...toArray(rawValue)]
    const dedupedValues = Array.from(new Set(mergedValues))

    if (dedupedValues.length === 0) {
      return
    }
    normalized[key] =
      dedupedValues.length === 1 ? dedupedValues[0] : dedupedValues
  })

  return normalized
}

export const FilterDropdown = React.memo(
  ({
    filterParam,
    filterName,
    filterValues,
    isLoading,
    defaultSelection,
    show,
    handleToggle,
    includeParentFilter,
    isNested,
    isSingleSelect = false,
    icon,
    parentMeansAll,
    requiredCapabilities,
  }: FilterDropdownProps): JSX.Element => {
    const { capabilities, allowed } = useFilterCapabilities(
      filterParam,
      requiredCapabilities,
    )
    // Options with children open below them (accordion); several can be open.
    const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set())
    const toggleSubmenu = useCallback(
      (param: string) =>
        setOpenSubmenus((previous) => {
          const next = new Set(previous)
          next.has(param) ? next.delete(param) : next.add(param)
          return next
        }),
      [],
    )
    const [searchQuery, setSearchQuery] = useState('')
    // Inside a menu of filters (More filters), that menu's one search box applies:
    // this filter shows its matching options, all of them if its name matches, or
    // nothing at all.
    const menuSearch = useFilterMenuSearch()
    const inMenuSearch = !!isNested && menuSearch !== null
    const query = (inMenuSearch ? menuSearch : searchQuery).trim()
    const nameMatches =
      !!query && filterName.toLowerCase().includes(query.toLowerCase())
    const hasSearch =
      !inMenuSearch &&
      countFilterOptions(filterValues) > FILTER_MENU_SEARCH_THRESHOLD
    const visibleValues = useMemo(
      () =>
        nameMatches ? filterValues : filterOptionsBySearch(filterValues, query),
      [filterValues, query, nameMatches],
    )
    const isSearching = !!query && !nameMatches
    const isHiddenBySearch =
      inMenuSearch && isSearching && !visibleValues.length
    const [isOpen, setIsOpen] = useState(false)
    const [internalMultiOpen, setInternalMultiOpen] = useState(false)
    const isMultiExternallyControlled = handleToggle !== undefined
    const location = useLocation()
    const searchParamsFallback = useMemo(() => {
      return queryString.parse(location.search, {
        arrayFormat: 'bracket',
      })
    }, [location])
    const {
      navigateState: {
        parsedQueryParams,
        locationSearchString: contextLocationSearchString,
      },
    } = useNavigateContext()
    const searchParams = useMemo(
      () => normalizeParsedQuery(parsedQueryParams || searchParamsFallback),
      [parsedQueryParams, searchParamsFallback],
    )
    const locationSearchString = contextLocationSearchString ?? location.search

    // For single-select, we need to handle the value differently
    const appliedFilters: Record<string, string[] | string> = useMemo(() => {
      if (isSingleSelect) {
        const appliedFilters: Record<string, string> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          // If it's an array, take the first value (shouldn't happen but handle it)
          appliedFilters[filterParam] = preprocessed[0] || ''
        } else {
          appliedFilters[filterParam] = (preprocessed as string) || ''
        }
        return appliedFilters
      } else {
        const appliedFilters: Record<string, string[]> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          appliedFilters[filterParam] = preprocessed
        } else {
          appliedFilters[filterParam] = preprocessed ? [preprocessed] : []
        }
        if (!appliedFilters[filterParam].length && defaultSelection?.length) {
          appliedFilters[filterParam] = defaultSelection
        }
        filterValues.forEach((value) => {
          const nestedParam = value.nestedFilter?.filterParam
          if (nestedParam && searchParams[nestedParam]) {
            const nestedValue = searchParams[nestedParam]
            appliedFilters[nestedParam] = Array.isArray(nestedValue)
              ? (nestedValue as string[])
              : [nestedValue as string]
          }
        })
        return appliedFilters
      }
    }, [
      searchParams,
      filterParam,
      filterValues,
      isSingleSelect,
      defaultSelection,
    ])

    const isSmartCheckFilter = filterParam === FilterParam.AI_CONTROL_CHECKS

    // For single-select, get the selected value's label
    const selectedValueLabel = useMemo(() => {
      if (!isSingleSelect) return null
      const selectedValue = appliedFilters[filterParam] as string
      if (!selectedValue) return null
      const selectedItem = filterValues.find(
        (item) => item.value === selectedValue,
      )
      return selectedItem?.label || null
    }, [isSingleSelect, appliedFilters, filterParam, filterValues])

    // Determine if there's a selection
    const appliedFilterCount = useMemo(
      () =>
        Array.from(
          new Set(
            Object.values(appliedFilters)
              .flat()
              .filter((value): value is string => typeof value === 'string'),
          ),
        ).length,
      [appliedFilters],
    )

    const hasSelection = isSingleSelect
      ? !!(appliedFilters[filterParam] as string)
      : appliedFilterCount > 0

    const { registerFilter } = useFilterLabels()

    // Register display name + full value map so SavedFiltersModal can
    // show e.g. "Department: Engineering, IT" for any saved filter.
    useEffect(() => {
      const valueMap: Record<string, string> = {}
      filterValues.forEach((fv) => {
        if (typeof fv.label === 'string' && !fv.isGroupHeading)
          valueMap[fv.value] = fv.label
      })
      if (Object.keys(valueMap).length > 0) {
        registerFilter(filterParam, filterName, valueMap)
      }
    }, [filterValues, filterParam, filterName, registerFilter])

    // Single-select: internal open state. Multi-select: parent-controlled or internal
    // so react-bootstrap always receives an explicit `show` and root-close works reliably.
    const multiSelectShow = isMultiExternallyControlled
      ? show
      : internalMultiOpen
    const dropdownShow =
      (inMenuSearch && !!query) || (isSingleSelect ? isOpen : multiSelectShow)

    // The menus close on an outside MOUSEDOWN (rootCloseEvent), and the toggle
    // is outside its own menu. So clicking an open filter's toggle closed it on
    // mousedown, then the click toggled it open again: it never closed. Leave
    // that one to the toggle's own click.
    const toggleRef = useRef<HTMLButtonElement>(null)
    const dropdownOnToggle = useCallback(
      (nextShow: boolean, meta?: ToggleMetadata) => {
        const target = meta?.originalEvent?.target
        if (
          !nextShow &&
          meta?.source === 'rootClose' &&
          meta.originalEvent?.type === 'mousedown' &&
          target instanceof Node &&
          toggleRef.current?.contains(target)
        ) {
          return
        }
        if (!nextShow) {
          setOpenSubmenus(new Set())
          setSearchQuery('')
        }
        if (isSingleSelect) {
          setIsOpen(nextShow)
        } else if (isMultiExternallyControlled) {
          handleToggle(nextShow)
        } else {
          setInternalMultiOpen(nextShow)
        }
      },
      [isSingleSelect, isMultiExternallyControlled, handleToggle],
    )

    if (isHiddenBySearch) return <></>

    return (
      <FilterCapabilitiesContext.Provider value={capabilities}>
        <Dropdown
          autoClose={isSingleSelect ? true : 'outside'}
          show={dropdownShow}
          onToggle={dropdownOnToggle}
        >
          <Dropdown.Toggle
            ref={toggleRef}
            disabled={!allowed}
            as={FilterDropdownToggle}
            $isAi={isSmartCheckFilter}
            id={`${filterName}-dropdown`}
            $hasSelection={hasSelection}
            $isNested={isNested}
          >
            <FilterDropdownToggleContent>
              {icon && <FilterDropdownIcon>{icon}</FilterDropdownIcon>}
              {isSingleSelect && selectedValueLabel ? (
                <p>
                  {filterName}: {selectedValueLabel}
                </p>
              ) : (
                <>
                  <p>{filterName}</p>
                  {!isSingleSelect && appliedFilterCount > 0 && (
                    <FilterCountChip ml="xxs">
                      {appliedFilterCount}
                    </FilterCountChip>
                  )}
                </>
              )}
            </FilterDropdownToggleContent>
          </Dropdown.Toggle>
          {isLoading ? (
            <Dropdown.Menu rootCloseEvent="mousedown">
              <MenuPlaceholder height={108} />
            </Dropdown.Menu>
          ) : (
            // Children open below their option, so nothing pops out of the menu.
            <DropDownMenuWithNesting $hasNested={false}>
              {hasSearch && (
                <DropdownSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={`Search ${filterName.toLowerCase()}...`}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <FilterItemsContainer>
                {isSearching && visibleValues.length === 0 && (
                  <NoFilterMatches>No matches found.</NoFilterMatches>
                )}
                {isSingleSelect ? (
                  <SingleSelectDropdownItems
                    filterParam={filterParam}
                    filterValues={visibleValues}
                    location={location}
                    searchParams={searchParams}
                    appliedFilters={appliedFilters as Record<string, string>}
                    onSelect={() => isSingleSelect && setIsOpen(false)}
                  />
                ) : (
                  <MultiSelectDropdownItems
                    filterParam={filterParam}
                    filterValues={visibleValues}
                    location={location}
                    searchParams={searchParams}
                    appliedFilters={appliedFilters as Record<string, string[]>}
                    openSubmenus={openSubmenus}
                    toggleSubmenu={toggleSubmenu}
                    isSearching={isSearching}
                    locationSearchString={locationSearchString}
                    includeParentFilter={includeParentFilter}
                    parentMeansAll={parentMeansAll}
                  />
                )}
              </FilterItemsContainer>
            </DropDownMenuWithNesting>
          )}
        </Dropdown>
      </FilterCapabilitiesContext.Provider>
    )
  },
)

export const SimpleDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  replaceParam?: boolean
}): JSX.Element => {
  const { allowed } = useFilterCapabilities(filterParam)
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: replaceParam
          ? [filterValue.value]
          : Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        aria-disabled={!allowed}
        tabIndex={allowed ? 0 : -1}
        to={href}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (!allowed) return
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        <FilterOptionLabel>{filterValue.label}</FilterOptionLabel>
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SimpleDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  replaceParam?: boolean
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) => (
      <SimpleDropdownItemComponent
        key={filterValue.value}
        filterParam={filterParam}
        filterValue={filterValue}
        appliedFilters={appliedFilters}
        baseQuery={searchParams}
        location={location}
        replaceParam={replaceParam}
      />
    ))}
  </>
)

/**
 * Compact "select all" for a parent whose own value is itself a filter: writes
 * only the toggled parent value
 */
const buildParentMeansAllHref = ({
  pathname,
  baseQuery,
  filterParam,
  nestedParam,
  value,
  isChecked,
}: {
  pathname: string
  baseQuery?: queryString.ParsedQuery<string>
  filterParam: FilterParam | string
  nestedParam: FilterParam | string
  value: string
  isChecked: boolean
}): string => {
  const parentValues = new Set(asArray(baseQuery?.[filterParam]))
  if (isChecked) {
    parentValues.delete(value)
  } else {
    parentValues.add(value)
  }
  return queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [filterParam]: Array.from(parentValues),
        [nestedParam]: [],
      },
    },
    { arrayFormat: 'bracket' },
  )
}

/**
 * Select/deselect every child, and select/deselect just the parent value.
 *  `includeParentFilter` keeps the parent value alongside the children.
 */
const buildNestedParentHrefs = ({
  pathname,
  baseQuery,
  locationSearchString,
  filterParam,
  nestedFilter,
  value,
  appliedFilters,
  includeParentFilter,
}: {
  pathname: string
  baseQuery?: queryString.ParsedQuery<string>
  locationSearchString: string
  filterParam: FilterParam | string
  nestedFilter: NestedFilterType
  value: string
  appliedFilters: Record<string, string[]>
  includeParentFilter?: boolean
}): {
  selectAllHref: string
  deselectAllHref: string
  selectSelfHref: string
  deselectSelfHref: string
} => {
  const selectAllHref = queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [nestedFilter.filterParam]: Array.from(
          nestedFilter.filterValues
            .map((f) => f.value)
            .filter((f) => f !== 'custom') || [],
        ),
        ...(includeParentFilter && {
          [filterParam]: [...(appliedFilters[filterParam] || []), value].filter(
            (v, index, array) => array.indexOf(v) === index,
          ),
        }),
      },
    },
    { arrayFormat: 'bracket' },
  )
  const deselectAllHref = includeParentFilter
    ? queryString.stringifyUrl(
        {
          url: pathname,
          query: {
            ...baseQuery,
            [nestedFilter.filterParam]: [],
            [filterParam]:
              appliedFilters[filterParam]?.filter((f) => f !== value) || [],
          },
        },
        { arrayFormat: 'bracket' },
      )
    : queryString.exclude(
        `${pathname}${locationSearchString}`,
        [nestedFilter.filterParam],
        { arrayFormat: 'bracket' },
      )
  const selectSelfHref = queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [filterParam]: [value],
      },
    },
    { arrayFormat: 'bracket' },
  )
  const deselectSelfHref = includeParentFilter
    ? queryString.stringifyUrl(
        {
          url: pathname,
          query: {
            ...baseQuery,
            [filterParam]:
              appliedFilters[filterParam]?.filter((f) => f !== value) || [],
          },
        },
        { arrayFormat: 'bracket' },
      )
    : queryString.exclude(`${pathname}${locationSearchString}`, [filterParam], {
        arrayFormat: 'bracket',
      })
  return { selectAllHref, deselectAllHref, selectSelfHref, deselectSelfHref }
}

export const MultiSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  openSubmenus,
  toggleSubmenu,
  isSearching,
  locationSearchString,
  includeParentFilter,
  parentMeansAll,
  ensureParent,
  partialFilters,
}: {
  filterParam: FilterParam | string
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  openSubmenus?: Set<string>
  toggleSubmenu?: (param: string) => void
  // While the menu is being searched, every option's matching children show.
  isSearching?: boolean
  locationSearchString: string
  includeParentFilter?: boolean
  parentMeansAll?: boolean
  /**
   * Set on nested (child) items under a `parentMeansAll` parent: selecting this
   * child also ensures the parent stays selected while a specific requirement subset is active.
   */
  ensureParent?: EnsureParent
  partialFilters?: Record<string, string[]>
}): JSX.Element => {
  const { allowed: parentAllowed } = useFilterCapabilities(filterParam)
  const { allowed: nestedAllowed } = useFilterCapabilities(
    filterValue.nestedFilter?.filterParam || '',
  )
  const allowed = parentAllowed && nestedAllowed
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (!partialFilters &&
      filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  const isIndeterminate =
    !isChecked && !!partialFilters?.[filterParam]?.includes(filterValue.value)
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: Array.from(newAppliedFilters),
        // A nested (child) item under a `parentMeansAll` parent keeps the
        // parent's value present
        ...(ensureParent && {
          [ensureParent.filterParam]: Array.from(
            new Set([
              ...asArray(baseQuery?.[ensureParent.filterParam]),
              ensureParent.value,
            ]),
          ),
        }),
      },
    },
    { arrayFormat: 'bracket' },
  )

  const nestedFilterHasValues =
    filterValue.nestedFilter && !!filterValue.nestedFilter.filterValues?.length
  const nestedParam = filterValue.nestedFilter?.filterParam ?? ''
  const isSubmenuOpen =
    !!nestedFilterHasValues &&
    (!!isSearching || !!openSubmenus?.has(nestedParam))

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        aria-disabled={!allowed}
        tabIndex={allowed ? 0 : -1}
        to={href}
        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (!allowed) return
          if (filterValue.nestedFilter && nestedFilterHasValues) {
            if (!isSearching) toggleSubmenu?.(nestedParam)
            return
          }
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        <CheckBoxInput
          disabled={!allowed}
          id={filterValue.value}
          label={<FilterOptionLabel>{filterValue.label}</FilterOptionLabel>}
          checked={isChecked}
          indeterminate={isIndeterminate}
          onClick={(e) => {
            e.stopPropagation()
            if (!allowed) {
              e.preventDefault()
              return
            }
            if (isCustom) {
              filterValue.customOnClick?.()
              return
            }
          }}
          onChange={async (e) => {
            if (!allowed) {
              e.preventDefault()
              return
            }
            if (isCustom) {
              e.preventDefault()
              return
            }
            if (parentMeansAll && filterValue.nestedFilter) {
              navigate(
                buildParentMeansAllHref({
                  pathname: location.pathname,
                  baseQuery,
                  filterParam,
                  nestedParam: filterValue.nestedFilter.filterParam,
                  value: filterValue.value,
                  isChecked,
                }),
                { replace: true },
              )
              return
            }
            if (filterValue.nestedFilter) {
              const {
                selectAllHref,
                deselectAllHref,
                selectSelfHref,
                deselectSelfHref,
              } = buildNestedParentHrefs({
                pathname: location.pathname,
                baseQuery,
                locationSearchString,
                filterParam,
                nestedFilter: filterValue.nestedFilter,
                value: filterValue.value,
                appliedFilters,
                includeParentFilter,
              })
              if (!isChecked) {
                navigate(
                  nestedFilterHasValues ? selectAllHref : selectSelfHref,
                  {
                    replace: true,
                  },
                )
              } else {
                navigate(
                  nestedFilterHasValues ? deselectAllHref : deselectSelfHref,
                  { replace: true },
                )
              }
              return
            }
            navigate(href, { replace: true })
          }}
          tabIndex={-1}
        />
        {filterValue.nestedFilter && nestedFilterHasValues && (
          <span style={{ padding: '0 8px' }}>
            <FilterAccordionChevron isOpen={isSubmenuOpen} />
          </span>
        )}
      </FilterDropdownItem>
      {!!filterValue.nestedFilter && isSubmenuOpen && (
        <FilterAccordionBody>
          <NestedFilter
            filterParam={filterValue.nestedFilter.filterParam}
            filterValues={filterValue.nestedFilter.filterValues}
            ensureParent={
              parentMeansAll
                ? { filterParam, value: filterValue.value }
                : undefined
            }
            isSearching={isSearching}
          />
        </FilterAccordionBody>
      )}
    </FilterOptionContainer>
  )
}

export const MultiSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  openSubmenus,
  toggleSubmenu,
  isSearching,
  locationSearchString,
  includeParentFilter,
  parentMeansAll,
  ensureParent,
  partialFilters,
}: {
  filterParam: FilterParam | string
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  openSubmenus: Set<string>
  toggleSubmenu: (param: string) => void
  isSearching?: boolean
  locationSearchString: string
  includeParentFilter?: boolean
  parentMeansAll?: boolean
  ensureParent?: EnsureParent
  partialFilters?: Record<string, string[]>
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) =>
      filterValue.isGroupHeading ? (
        <FilterGroupHeading key={filterValue.value}>
          {filterValue.label}
        </FilterGroupHeading>
      ) : (
        <MultiSelectDropdownItemComponent
          key={filterValue.value}
          filterParam={filterParam}
          filterValue={filterValue}
          appliedFilters={appliedFilters}
          baseQuery={searchParams}
          location={location}
          openSubmenus={openSubmenus}
          toggleSubmenu={toggleSubmenu}
          isSearching={isSearching}
          locationSearchString={locationSearchString}
          includeParentFilter={includeParentFilter}
          parentMeansAll={parentMeansAll}
          ensureParent={ensureParent}
          partialFilters={partialFilters}
        />
      ),
    )}
  </>
)

export const SingleSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  onSelect,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  onSelect?: () => void
}): JSX.Element => {
  const { allowed } = useFilterCapabilities(filterParam)
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const selectedValue = appliedFilters[filterParam]
  const isSelected = selectedValue === filterValue.value
  const isCustom = isFunction(filterValue.customOnClick)

  // For single-select: if clicking the same item, deselect it (remove from URL)
  // Otherwise, select the new item
  const newValue = isSelected ? undefined : filterValue.value

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        ...(newValue ? { [filterParam]: newValue } : {}),
      },
    },
    { skipNull: true },
  )

  // Remove the param if deselecting
  const finalHref = isSelected
    ? queryString.exclude(
        `${location.pathname}${location.search}`,
        [filterParam],
        { arrayFormat: 'bracket' },
      )
    : href

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        aria-disabled={!allowed}
        tabIndex={allowed ? 0 : -1}
        to={finalHref}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (!allowed) return
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(finalHref, { replace: true })
          }
          onSelect?.()
        }}
      >
        <FilterOptionLabel>{filterValue.label}</FilterOptionLabel>
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SingleSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  onSelect,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string>
  onSelect?: () => void
}): JSX.Element => (
  <>
    {filterValues.map((filterValue, index) => (
      <React.Fragment key={filterValue.value}>
        {filterValue.divider && index > 0 && <Dropdown.Divider />}
        <SingleSelectDropdownItemComponent
          filterParam={filterParam}
          filterValue={filterValue}
          appliedFilters={appliedFilters}
          baseQuery={searchParams}
          location={location}
          onSelect={onSelect}
        />
      </React.Fragment>
    ))}
  </>
)
